import { triggerPrefetch } from './admin/prefetch';
import { Article, CreateArticlePayload, UpdateArticlePayload, createArticle, updateArticle } from './articles';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function normStr(v: any): string {
  return String(v ?? '').trim();
}

function normalizeAttachments(aRaw: any): Array<{ name: string; url: string; mime_type?: string; size?: number }> | undefined {
  try {
    const arr = Array.isArray(aRaw) ? aRaw : (typeof aRaw === 'string' ? JSON.parse(aRaw) : []);
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    return arr.map((it: any) => {
      if (typeof it === 'string') {
        const name = it.split('/').pop() || 'soubor';
        return { name, url: it };
      }
      const name = it?.name || (String(it?.url || '').split('/').pop() || 'soubor');
      const url = String(it?.url || '');
      const mime_type = it?.mime_type || it?.type;
      const size = typeof it?.size === 'number' ? it.size : undefined;
      return { name, url, mime_type, size };
    });
  } catch {
    return undefined;
  }
}

function normalizeGalleryIds(raw: any): string[] | undefined {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {}
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return undefined;
}

function buildUpdatePayload(editing: Partial<Article>): UpdateArticlePayload {
  const attachments = normalizeAttachments((editing as any)?.attachments);
  const galleryIds = normalizeGalleryIds((editing as any)?.gallery_photo_ids);
  return {
    title: normStr((editing as any)?.title),
    content: typeof (editing as any)?.content === 'string' ? (editing as any).content : '',
    image_url: normStr((editing as any)?.image_url),
    ...(typeof (editing as any)?.category_id === 'number' ? { category_id: (editing as any).category_id } : {}),
    category_name: normStr((editing as any)?.category_name),
    slug: normStr((editing as any)?.slug),
    seo_title: normStr((editing as any)?.seo_title),
    seo_description: normStr((editing as any)?.seo_description),
    og_image_url: normStr((editing as any)?.og_image_url),
    featured: !!(editing as any)?.featured,
    // Gallery
    gallery_album_id: normStr((editing as any)?.gallery_album_id),
    gallery_album_url: normStr((editing as any)?.gallery_album_url),
    ...(galleryIds ? { gallery_photo_ids: galleryIds } : {}),
    // YouTube
    youtube_video_id: normStr((editing as any)?.youtube_video_id),
    youtube_video_title: normStr((editing as any)?.youtube_video_title),
    youtube_video_url: normStr((editing as any)?.youtube_video_url),
    youtube_video_thumbnail: normStr((editing as any)?.youtube_video_thumbnail),
    // Attachments
    ...(attachments ? { attachments } : {}),
  } as UpdateArticlePayload;
}

function buildCreatePayload(editing: Partial<Article>): CreateArticlePayload {
  const u = buildUpdatePayload(editing);
  return u as unknown as CreateArticlePayload;
}

export async function saveArticleReliable(editing: Partial<Article>): Promise<Article> {
  const id = (editing as any)?.id;
  const isUpdate = !!id;
  const payloadU = buildUpdatePayload(editing);
  const payloadC = buildCreatePayload(editing);

  const maxAttempts = 3;
  let lastErr: any;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      let saved: Article;
      if (isUpdate) {
        try {
          saved = await updateArticle(id as any, payloadU);
        } catch (e: any) {
          if (e?.response?.status === 404) {
            saved = await createArticle(payloadC);
          } else {
            throw e;
          }
        }
      } else {
        saved = await createArticle(payloadC);
      }

      if (saved?.published) {
        try { await triggerPrefetch(); } catch {}
      }
      return saved;
    } catch (e) {
      lastErr = e;
      if (attempt < maxAttempts) {
        await sleep(attempt * 400);
        continue;
      }
      throw e;
    }
  }
  throw lastErr;
}
